import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { formGroupPatchValueNullFriendly, getObjectProperty } from 'app/helpers/utils';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { ConnectedApp, ConnectedAppAuth, ConnectedAppEndpoint, ConnectedAppParameter, ConnectedAppResponse } from 'app/models/connected-app';
import { ConnectedAppService } from 'app/services/connected-app.service';
import { SwaggerImportService } from 'app/services/swagger-import.service';
import { ToastService } from 'ng-uikit-pro-standard';
import { concat, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

declare var SwaggerClient: Function;
declare var marked: any;

@Component({
  selector: 'app-add-edit-connected-app',
  templateUrl: './add-edit-connected-app.component.html',
  styleUrls: ['./add-edit-connected-app.component.scss']
})
export class AddEditConnectedAppComponent implements OnInit, AfterViewInit {

  isLoading: boolean = false;
  appId: number = -1;

  importedFileName: string = '';

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

  connectedApp: ConnectedApp = null;
  connectedAppFormGroup: FormGroup = null;

  tableLimitSelect = [5, 10, 20, 30, 50, 100];
  selectedTableLimit: number = 5;
  
  tableLoadingIndicator: boolean = false;

  constructor(private connectedAppService: ConnectedAppService, private swaggerImportService: SwaggerImportService, 
    private validationHandler: ValidationHandler, private activatedRoute: ActivatedRoute, private toastService: ToastService,
    private router: Router) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.appId = -1;
    this.activatedRoute.params.subscribe(params => {
      let appId: string = params['appId'];
      this.appId = -1;
      this.isLoading = false;
  
      if (appId != 'new') {
        this.appId = +appId;
        this.isLoading = true;
        this.connectedAppService.getById(this.appId).pipe(finalize(() => this.isLoading = false)).subscribe((c) => {
          this.connectedAppFormGroup = this.connectedAppService.formGroup(c);
          this.connectedApp = this.connectedAppFormGroup.value;
        }, (err) => {
          this.toastService.error('Error loading connected app settings!');
        });
      }
    });

  }

  ngAfterViewInit() {
  }

  openChooseLocalFile() {
    (<HTMLElement>this.fileInput.nativeElement).click();
  }

  filesPicked(files: File[]) {
    if (!files || files.length == 0) {
      this.fileInput.nativeElement.value = "";
      this.connectedApp = null;
      return;
    }

    let selectedFile: File = files[0];
    this.fileInput.nativeElement.value = "";
    this.importedFileName = selectedFile.name;

    this.isLoading = true;
    var json = null;
    var reader = new FileReader();
    reader.readAsText(selectedFile, "UTF-8");
    reader.onload = (evt) => {
      try {
        json = JSON.parse(<string>evt.target.result);
        SwaggerClient({spec: json}).then((client) => {
          this.isLoading = false;
          let importedAppFormGroup = this.swaggerImportService.getFormGroupFromSpec(client.spec);
          if (this.appId != -1) {
            // map IDs for proper edit in place
            // App ID
            importedAppFormGroup.get('id').setValue(this.connectedApp.id);
            // Auth IDs
            let importedAuths: ConnectedAppAuth[] = importedAppFormGroup.get('auths').value || [];
            importedAuths.forEach((importedAuth, authIndex) => {
              let originalAuth: ConnectedAppAuth = this.connectedApp.auths.find(orgAuth => orgAuth.name == importedAuth.name && orgAuth.type == importedAuth.type);
              if (originalAuth) {
                importedAppFormGroup.get(`auths.${authIndex}.id`).setValue(originalAuth.id);
                importedAppFormGroup.get(`auths.${authIndex}.oauth2_client_id`).setValue(originalAuth.oauth2_client_id);
                importedAppFormGroup.get(`auths.${authIndex}.oauth2_client_secret`).setValue(originalAuth.oauth2_client_secret);
              }
            });

            // Endpoints
            let importedEndpoints: ConnectedAppEndpoint[] = importedAppFormGroup.get('endpoints').value || [];
            importedEndpoints.forEach((importedEndpoint, endpointIndex) => {
              let originalEndpoint: ConnectedAppEndpoint = this.connectedApp.endpoints.find(orgEndpoint => orgEndpoint.url == importedEndpoint.url && orgEndpoint.http_verb == importedEndpoint.http_verb);
              if (originalEndpoint) {
                importedEndpoint.id = originalEndpoint.id;

                // map responses
                let importedResponses: ConnectedAppResponse[] = importedEndpoint.responses || [];
                importedResponses.forEach((importedResponse, responseIndex) => {
                  let originalResponse: ConnectedAppResponse = originalEndpoint.responses.find(orgResponse => orgResponse.code == importedResponse.code);
                  if (originalResponse) {
                    importedEndpoint.responses[responseIndex].id = originalResponse.id;
                  }
                });

                // map parameters
                let importedParameters: ConnectedAppParameter[] = importedEndpoint.parameters || [];
                importedParameters.forEach((importedParameter, parameterIndex) => {
                  let originalParameter: ConnectedAppParameter = originalEndpoint.parameters.find(orgParameter => orgParameter.name == importedParameter.name && orgParameter.in == importedParameter.in);
                  if (originalParameter) {
                    importedEndpoint.parameters[parameterIndex].id = originalParameter.id;
                  }
                });                
              }
            });
            importedAppFormGroup.get('endpoints').setValue(importedEndpoints);
          }
          this.connectedAppFormGroup = importedAppFormGroup;
          this.connectedApp = this.connectedAppFormGroup.value;
        })
        .catch((ex) => {
          this.toastService.error('Error reading spec. file!');
          this.isLoading = false;
          console.log(ex); //We get the exception "TypeError [ERR_INVALID_ARG_TYPE]: The "url" argument must be of type string. Received type undefined"
        });
      }

      catch(e){
        this.isLoading = false;
        this.toastService.error('Error parsing spec. file!');
        console.log(e);
      }
    };  
  }

  getMarkdownHtml(propertyPath: string, el: HTMLElement) {
    let value: string = getObjectProperty(this.connectedApp, propertyPath) || '';
    el.innerHTML = marked.parse(value);
  }

  saveConnectedApp() {
    this.validationHandler.onSubmitCheck(this.connectedAppFormGroup);
    if(!this.connectedAppFormGroup.valid) {
      return;
    }

    this.isLoading = true;
    let connectedAppToSave: ConnectedApp = this.connectedAppFormGroup.value;
    if (connectedAppToSave.endpoints.length <= 20) {
      this.connectedAppService.put(this.connectedAppFormGroup.value).pipe(finalize(() => this.isLoading = false)).subscribe(() => {
        this.router.navigate(['/settings']);
      }, (e) => {
        this.toastService.error('Error saving connected app!');
        console.log(e);
      });
    } else {
      // need to chunk it
      let fullEndpoints = connectedAppToSave.endpoints;
      delete connectedAppToSave.endpoints;
      this.connectedAppService.put(this.connectedAppFormGroup.value).subscribe((c) => {
        this.connectedAppFormGroup.get('id').setValue(c.id);
        let observablesArray = [];
        while (fullEndpoints.length) {
          observablesArray.push(this.connectedAppService.addEndpointsToApp(c.id, fullEndpoints.splice(0, 20)));
        }
        concat(...observablesArray).pipe(finalize(() => this.isLoading = false)).subscribe(undefined, (e) => {
          this.toastService.error('Error saving endpoints!');
          console.log(e);
        }, () => {
          this.router.navigate(['/settings']);
        });
      }, (err) => {
        this.isLoading = false;
        this.toastService.error('Error saving connected app!');
        console.log(err);
      });
    }
  }
}
